/**
 * 3D card tilt effect with reduced motion support
 */

import { prefersReducedMotion } from '../core/utils.js';

export class CardTilt {
  #cards;
  #abortController = new AbortController();

  constructor() {
    this.#cards = document.querySelectorAll('[data-tilt]');
    
    if (prefersReducedMotion() || this.#cards.length === 0) {
      return;
    }

    this.#init();
  }

  #init() {
    const signal = this.#abortController.signal;

    this.#cards.forEach(card => {
      card.dataset.tiltInitialized = 'true';
      
      // Mouse move for tilt
      card.addEventListener('mousemove', (e) => this.#handleMouseMove(e, card), { passive: true, signal });
      
      // Reset on leave
      card.addEventListener('mouseleave', () => this.#handleMouseLeave(card), { signal });
      
      // Keyboard equivalent
      card.addEventListener('focus', () => this.#handleFocus(card), { signal });
      card.addEventListener('blur', () => this.#handleMouseLeave(card), { signal });
    });
  }

  #handleMouseMove(e, card) {
    const rect = card.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;

    // Apply tilt
    card.style.transform = `perspective(1000px) rotateY(${x * 10}deg) rotateX(${-y * 10}deg) translateY(-8px)`;

    // Move background
    const bg = card.querySelector('.project-card-bg');
    if (bg) {
      bg.style.transform = `scale(1.05) translate(${x * -10}px, ${y * -10}px)`;
    }
  }

  #handleMouseLeave(card) {
    card.style.transform = '';
    const bg = card.querySelector('.project-card-bg');
    if (bg) {
      bg.style.transform = '';
    }
  }

  #handleFocus(card) {
    // Subtle lift effect for keyboard focus
    card.style.transform = 'translateY(-8px)';
  }

  refresh() {
    // Re-query for new cards and attach listeners
    const newCards = document.querySelectorAll('[data-tilt]');
    const signal = this.#abortController.signal;
    
    newCards.forEach(card => {
      // Skip if already has listeners (check for custom attribute)
      if (card.dataset.tiltInitialized) return;
      card.dataset.tiltInitialized = 'true';
      
      card.addEventListener('mousemove', (e) => this.#handleMouseMove(e, card), { passive: true, signal });
      card.addEventListener('mouseleave', () => this.#handleMouseLeave(card), { signal });
      card.addEventListener('focus', () => this.#handleFocus(card), { signal });
      card.addEventListener('blur', () => this.#handleMouseLeave(card), { signal });
    });
    
    this.#cards = newCards;
  }

  destroy() {
    this.#abortController.abort();
    
    this.#cards.forEach(card => {
      card.style.transform = '';
      const bg = card.querySelector('.project-card-bg');
      if (bg) bg.style.transform = '';
      delete card.dataset.tiltInitialized;
    });
  }
}
